/**
 * WelcomePage Component
 *
 * Feature: 011-registration-welcome
 *
 * Main welcome page shown after user registration. Includes:
 * - Personalized greeting and BOSI tagline (WelcomeHeader)
 * - Call-to-action button (See My BOSI Plan)
 * - Tracking of page views for analytics
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { updateOnboardingStatus } from '../../services/api';
import WelcomeHeader from './WelcomeHeader';
import FeatureCard from './FeatureCard';
import { featureData } from './featureData';

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track page view on mount (T020)
  useEffect(() => {
    const trackView = async () => {
      try {
        await updateOnboardingStatus('viewed');
      } catch (err) {
        // Silent fail - don't block UX for analytics
        console.error('Failed to track welcome page view:', err);
      }
    };

    trackView();
  }, []); // Run once on mount

  // Handle "See My BOSI Plan" button click (T018)
  // Updated for Feature 017: Navigate to body insight pages instead of directly to check-in
  const handleStartCheckIn = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      setError(null);

      // Track that user clicked "Start" on welcome page
      await updateOnboardingStatus('viewed');

      // Navigate to body insight pages (Feature 017)
      navigate('/body-insight/1');
    } catch (err: any) {
      console.error('Failed to navigate:', err);
      setError('Failed to continue. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF5E9]">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  // Use first name if available, fall back to email prefix
  const userName = user.profile?.first_name || user.email.split('@')[0];

  return (
    <div className="min-h-screen bg-[#FFF5E9] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Welcome Header */}
        <WelcomeHeader userName={userName} />

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Key Features Overview Section (T026, T027) */}
        <div className="my-12">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8" style={{ fontFamily: 'Nunito, sans-serif' }}>
            What You Can Do with BOSI
          </h2>

          {/* Responsive Grid Layout: 1 column (mobile), 2 columns (tablet), 3 columns (desktop) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureData.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>

        {/* Call-to-Action Button */}
        <div className="flex justify-center items-center mt-12">
          {/* Primary CTA: See My BOSI Plan */}
          <button
            onClick={handleStartCheckIn}
            disabled={isLoading}
            className="px-12 py-4 bg-[#C98CCC] text-white font-semibold text-lg rounded-lg
                     hover:bg-[#B87AB8] transition-colors duration-200 shadow-md
                     disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            {isLoading ? 'Loading...' : 'See My BOSI Plan'}
          </button>
        </div>

        {/* Subtle guidance text */}
        <p className="text-center text-gray-500 text-sm mt-8">
          You can always access this guide later from your settings
        </p>
      </div>
    </div>
  );
};

export default WelcomePage;
